import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useBrands } from '../contexts/BrandContext'
import { useBrandScope } from '../hooks/useBrandScope'
import { Field } from '../components/common/Field'
import { Forbidden } from '../components/common/Forbidden'
import { Icons } from '../components/icons'
import { can } from '../utils/permissions'
import { brandColor, brandInitial } from '../utils/brand'
import { notifySuccess, notifyError } from '../utils/notify'
import { BrandDomainService } from '../services/BrandDomainService'
import type { BrandDomain, BrandDomainList, BrandDomainStatus, DnsInstructions } from '../types'
import { Button } from '@/ui/button'
import { Label } from '@/ui/label'
import { Input } from '@/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/dialog'

/**
 * Status → Anzeige-Label + `.badge`-Variante (BMC-Designsystem `console.css`).
 * `error` nutzt die Basis-`.badge` mit rot getönten Inline-Werten (die
 * console.css-Badges kennen keine dedizierte Fehler-Variante).
 */
const STATUS_META: Record<BrandDomainStatus, { label: string; cls: 'active' | 'inactive' | 'warn' | 'error' }> = {
  pending: { label: 'Ausstehend', cls: 'inactive' },
  verifying: { label: 'Wird geprüft', cls: 'warn' },
  active: { label: 'Aktiv', cls: 'active' },
  error: { label: 'Fehler', cls: 'error' },
}

/** Status-Badge im console.css-`.badge`-Stil (mit rotem Sonderfall für `error`). */
function StatusBadge({ status }: { status: BrandDomainStatus }) {
  const meta = STATUS_META[status]
  if (meta.cls === 'error') {
    return (
      <span
        className="badge"
        style={{
          color: 'var(--red)',
          background: 'color-mix(in srgb, var(--red) 15%, transparent)',
          borderColor: 'color-mix(in srgb, var(--red) 26%, transparent)',
        }}
      >
        {meta.label}
      </span>
    )
  }
  return (
    <span className={`badge ${meta.cls}`}>
      {status === 'active' && <span className="dot" />}
      {meta.label}
    </span>
  )
}

/**
 * FQDN-Validierung (client-seitig, defensiv — der Server bleibt maßgeblich).
 * Bewusst ohne Regex/Backtracking: zeichenweise Prüfung (kein ReDoS-Risiko).
 */
function isDnsLabel(label: string): boolean {
  const n = label.length
  if (n === 0 || n > 63) return false
  if (label[0] === '-' || label[n - 1] === '-') return false
  for (const ch of label) {
    const ok = (ch >= 'a' && ch <= 'z') || (ch >= '0' && ch <= '9') || ch === '-'
    if (!ok) return false
  }
  return true
}
function isValidHostname(value: string): boolean {
  const host = value.trim().toLowerCase()
  if (host.length === 0 || host.length > 253) return false
  const labels = host.split('.')
  if (labels.length < 2) return false
  if (!labels.every(isDnsLabel)) return false
  // TLD rein alphabetisch (mind. 2 Zeichen).
  const tld = labels[labels.length - 1]
  if (tld.length < 2) return false
  for (const ch of tld) if (ch < 'a' || ch > 'z') return false
  return true
}

/** True, wenn der Fehler einen HTTP-409-Status (Conflict, Domain bereits vergeben) trägt. */
function isConflict(err: unknown): boolean {
  if (typeof err !== 'object' || err === null || !('response' in err)) return false
  return (err as { response?: { status?: number } }).response?.status === 409
}

function domainId(d: BrandDomain): string {
  return d._id ?? d.id ?? ''
}

/**
 * Domain-Verwaltung einer Marke (TP4 §1, ab TP6/5a admin-only). Der `brandKey`
 * kommt ausschließlich aus dem Routen-Parameter; admin ohne `brandKey`
 * (`/admin/domains`) erhält eine Marken-Auswahl. brand-admin darf Domains NICHT
 * verwalten — der Server-Scope (admin only) ist maßgeblich, die Seite spiegelt ihn.
 */
export function BrandDomainsPage() {
  const params = useParams<{ brandKey?: string }>()
  const { session } = useAuth()
  const { resolveBrand } = useBrands()
  const scope = useBrandScope()
  const navigate = useNavigate()

  const brandKey = params.brandKey ?? null
  const allowed = !!brandKey && !!session && can(session).domainManage()

  const [data, setData] = useState<BrandDomainList | null>(null)
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [hostname, setHostname] = useState('')
  const [hostnameError, setHostnameError] = useState<string | undefined>(undefined)
  const [adding, setAdding] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [removeTarget, setRemoveTarget] = useState<BrandDomain | null>(null)
  const [sendTarget, setSendTarget] = useState<BrandDomain | null>(null)
  const [sendEmail, setSendEmail] = useState('')

  const reload = useCallback(async () => {
    if (!brandKey) return
    try {
      setData(await BrandDomainService.list(brandKey))
      setLoadState('ready')
    } catch {
      setLoadState('error')
    }
  }, [brandKey])

  useEffect(() => {
    if (!allowed || !brandKey) return
    let cancelled = false
    setLoadState('loading')
    BrandDomainService.list(brandKey)
      .then((res) => {
        if (cancelled) return
        setData(res)
        setLoadState('ready')
      })
      .catch(() => {
        if (!cancelled) setLoadState('error')
      })
    return () => {
      cancelled = true
    }
  }, [allowed, brandKey])

  const handleAdd = useCallback(async () => {
    if (!brandKey) return
    const host = hostname.trim().toLowerCase()
    if (!isValidHostname(host)) {
      setHostnameError('Bitte eine gültige Domain angeben (z. B. karriere.marke.de).')
      return
    }
    setHostnameError(undefined)
    setAdding(true)
    try {
      await BrandDomainService.add(brandKey, host)
      notifySuccess(`Domain „${host}" hinzugefügt`)
      setHostname('')
      await reload()
    } catch (err) {
      notifyError(isConflict(err) ? 'Diese Domain ist bereits vergeben.' : 'Hinzufügen fehlgeschlagen, bitte erneut.')
    } finally {
      setAdding(false)
    }
  }, [brandKey, hostname, reload])

  const handleVerify = useCallback(
    async (d: BrandDomain) => {
      if (!brandKey) return
      setBusyId(domainId(d))
      try {
        const updated = await BrandDomainService.verify(brandKey, domainId(d))
        notifySuccess(
          updated.status === 'active'
            ? `Domain „${d.hostname}" verifiziert und aktiv`
            : `DNS noch nicht bestätigt (Status: ${STATUS_META[updated.status].label})`,
        )
        await reload()
      } catch {
        notifyError('Verifizierung fehlgeschlagen, bitte erneut.')
      } finally {
        setBusyId(null)
      }
    },
    [brandKey, reload],
  )

  const handleSetPrimary = useCallback(
    async (d: BrandDomain) => {
      if (!brandKey) return
      setBusyId(domainId(d))
      try {
        await BrandDomainService.setPrimary(brandKey, domainId(d))
        notifySuccess(`„${d.hostname}" ist jetzt die primäre Domain`)
        await reload()
      } catch {
        notifyError('Als primär setzen fehlgeschlagen, bitte erneut.')
      } finally {
        setBusyId(null)
      }
    },
    [brandKey, reload],
  )

  const handleRemove = useCallback(async () => {
    if (!brandKey || !removeTarget) return
    const target = removeTarget
    setBusyId(domainId(target))
    try {
      await BrandDomainService.remove(brandKey, domainId(target))
      notifySuccess(`Domain „${target.hostname}" entfernt`)
      setRemoveTarget(null)
      await reload()
    } catch {
      notifyError('Entfernen fehlgeschlagen, bitte erneut.')
    } finally {
      setBusyId(null)
    }
  }, [brandKey, removeTarget, reload])

  const handleSend = useCallback(async () => {
    if (!brandKey || !sendTarget) return
    setBusyId(domainId(sendTarget))
    try {
      await BrandDomainService.sendInstructions(brandKey, domainId(sendTarget), sendEmail.trim())
      notifySuccess(`DNS-Anleitung an ${sendEmail.trim()} gesendet`)
      setSendTarget(null)
      setSendEmail('')
    } catch {
      notifyError('Versand fehlgeschlagen, bitte erneut.')
    } finally {
      setBusyId(null)
    }
  }, [brandKey, sendTarget, sendEmail])

  const copy = useCallback(async (value: string, label: string) => {
    try {
      await navigator.clipboard?.writeText(value)
      notifySuccess(`${label} kopiert`)
    } catch {
      notifyError('Kopieren nicht möglich.')
    }
  }, [])

  if (!session) return null

  // admin ohne Marke → Auswahl; brand-admin/user ohne Marke → kein Zugriff.
  if (!brandKey) {
    if (scope.isAdmin) return <BrandPicker />
    return <Forbidden message="Für diese Ansicht ist keine Marke zugeordnet." />
  }
  if (!allowed) return <Forbidden message="Domains dieser Marke darfst du nicht verwalten." />

  const brand = resolveBrand(brandKey)
  const domains = data?.domains ?? []
  const instructionFor = (host: string): DnsInstructions | undefined =>
    data?.instructions.find((i) => i.hostname === host)

  return (
    <>
      <section className="cover">
        <div className="eyebrow">Domain-Verwaltung</div>
        <h1>
          Eigene <span className="hl">Domains</span> verwalten
        </h1>
        <p>
          Verbinde eine eigene Domain mit den vCards dieser Marke. Aktivierung erfolgt automatisch, sobald der
          DNS-Nachweis (TXT) greift.
        </p>
      </section>

      <section className="panel">
        <div className="head">
          <h2>Domain hinzufügen</h2>
          <span className="sub">{brand.brandName || brandKey}</span>
          <span className="spacer" />
          {scope.isAdmin && (
            <button type="button" className="btn ghost sm" onClick={() => navigate('/admin/domains')}>
              {Icons.back} Marke wechseln
            </button>
          )}
        </div>
        <div className="pad">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <Field
                id="domainHostname"
                label="Hostname"
                icon={Icons.web}
                type="text"
                value={hostname}
                onChange={(v) => setHostname(v)}
                error={hostnameError}
              />
            </div>
            <button type="button" className="btn primary" disabled={adding} onClick={handleAdd}>
              {Icons.plus} Hinzufügen
            </button>
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="head">
          <h2>Domains</h2>
          <span className="sub">{loadState === 'ready' ? domains.length : '–'}</span>
        </div>
        {loadState === 'loading' ? (
          <div className="empty">Domains werden geladen …</div>
        ) : loadState === 'error' ? (
          <div className="empty">
            Domains konnten nicht geladen werden.{' '}
            <button type="button" className="text-primary underline" onClick={() => void reload()}>
              Erneut versuchen
            </button>
          </div>
        ) : domains.length === 0 ? (
          <div className="empty">Noch keine Domains hinterlegt. Füge oben deine erste Domain hinzu.</div>
        ) : (
          <div className="list">
            {domains.map((d) => {
              const ins = instructionFor(d.hostname)
              const id = domainId(d)
              const busy = busyId === id
              return (
                <div key={id || d.hostname}>
                  <div className="lrow" style={{ cursor: 'default' }}>
                    <div className="who">
                      <div className="nm flex items-center gap-2">
                        <span className="mono truncate">{d.hostname}</span>
                        <StatusBadge status={d.status} />
                        {d.isPrimary && <span className="badge role">Primär</span>}
                      </div>
                      {d.lastError && <div className="mt">Letzter Fehler: {d.lastError}</div>}
                    </div>
                    <div className="side flex-wrap justify-end">
                      <button
                        type="button"
                        className="btn outline sm"
                        disabled={busy}
                        onClick={() => void handleVerify(d)}
                      >
                        Verifizieren
                      </button>
                      {!d.isPrimary && d.status === 'active' && (
                        <button
                          type="button"
                          className="btn outline sm"
                          disabled={busy}
                          onClick={() => void handleSetPrimary(d)}
                        >
                          Als primär
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn ghost sm"
                        disabled={busy}
                        onClick={() => {
                          setSendEmail('')
                          setSendTarget(d)
                        }}
                      >
                        Anleitung senden
                      </button>
                      <button
                        type="button"
                        className="btn ghost sm"
                        disabled={busy}
                        onClick={() => setRemoveTarget(d)}
                      >
                        Entfernen
                      </button>
                    </div>
                  </div>
                  {ins && (
                    <div className="section" style={{ margin: '2px 6px 10px' }}>
                      <div className="shead">
                        <span className="sico">{Icons.web}</span>
                        DNS-Einträge
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <DnsRow
                          label="CNAME"
                          name={ins.hostname}
                          value={ins.cnameTarget}
                          onCopy={() => void copy(ins.cnameTarget, 'CNAME')}
                          copyLabel="CNAME kopieren"
                        />
                        <DnsRow
                          label="TXT"
                          name={ins.txtName}
                          value={ins.txtValue}
                          onCopy={() => void copy(ins.txtValue, 'TXT-Wert')}
                          copyLabel="TXT-Wert kopieren"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* Entfernen-Bestätigung */}
      <Dialog open={removeTarget !== null} onOpenChange={(open: boolean) => !open && setRemoveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Domain entfernen</DialogTitle>
            <DialogDescription>
              Soll „{removeTarget?.hostname}" wirklich entfernt werden? Die Verbindung zu den vCards dieser Marke wird
              getrennt.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveTarget(null)}>
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={() => void handleRemove()}>
              Ja, entfernen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DNS-Anleitung per E-Mail versenden */}
      <Dialog
        open={sendTarget !== null}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setSendTarget(null)
            setSendEmail('')
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>DNS-Anleitung senden</DialogTitle>
            <DialogDescription>
              Die Einrichtungs-Anleitung für „{sendTarget?.hostname}" wird an die angegebene Adresse gesendet.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="f_sendEmail">E-Mail</Label>
            <Input
              id="f_sendEmail"
              type="email"
              value={sendEmail}
              onChange={(e) => setSendEmail(e.target.value)}
              placeholder="empfaenger@marke.de"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSendTarget(null)
                setSendEmail('')
              }}
            >
              Abbrechen
            </Button>
            <Button disabled={sendEmail.trim().length === 0} onClick={() => void handleSend()}>
              Senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/** Eine kopierbare DNS-Record-Zeile (Name + Wert, mono) — BMC-Designsystem `console.css`. */
function DnsRow({
  label,
  name,
  value,
  onCopy,
  copyLabel,
}: {
  label: string
  name: string
  value: string
  onCopy: () => void
  copyLabel: string
}) {
  return (
    <div className="flex items-start justify-between gap-2 rounded-lg bg-muted/50 px-3 py-2 text-xs">
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="mono truncate text-foreground" title={name}>
          {name}
        </div>
        <div className="mono truncate text-muted-foreground" title={value}>
          {value}
        </div>
      </div>
      <button
        type="button"
        className="btn ghost sm"
        style={{ padding: '0 10px' }}
        aria-label={copyLabel}
        onClick={onCopy}
      >
        {Icons.id}
      </button>
    </div>
  )
}

/** Marken-Auswahl für den admin auf `/admin/domains` (ohne brandKey). */
function BrandPicker() {
  const { brands } = useBrands()
  const navigate = useNavigate()
  return (
    <>
      <section className="cover">
        <div className="eyebrow">Domain-Verwaltung</div>
        <h1>Marke wählen</h1>
        <p>Wähle die Marke, deren Domains du verwalten möchtest.</p>
      </section>
      <section className="panel">
        <div className="head">
          <h2>Marken</h2>
          <span className="sub">{brands.length}</span>
        </div>
        {brands.length === 0 ? (
          <div className="empty">Keine Marken vorhanden.</div>
        ) : (
          <div className="list">
            {brands.map((b) => (
              <div className="lrow" key={b.brandKey}>
                <span className="pic" style={{ background: brandColor(b) }}>
                  {brandInitial(b)}
                </span>
                <div className="who">
                  <div className="nm">{b.brandName}</div>
                  <div className="mt">{b.brandKey}</div>
                </div>
                <div className="side">
                  <button
                    type="button"
                    className="btn outline sm"
                    onClick={() => navigate(`/admin/brands/${b.brandKey}/domains`)}
                  >
                    {Icons.domains} {b.brandName}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  )
}
